// packages/web-ui/src/AppShell/useReducedMotion.ts
//
// W2-S3 Task 8 (AC #8) — `useReducedMotion()` hook returning `true` when the
// user has set `prefers-reduced-motion: reduce`. Uses `useSyncExternalStore`
// for SSR-safe behaviour (React 19 native).
//
// W2-S2 already inlines a `prefersReducedMotion()` helper inside `AppShell.tsx`
// for the announcer's 200 ms delay. This story extracts the same pattern into
// a reusable hook for the badge fade, mode cross-fade, panel collapse, and
// skeleton shimmer (NFR-A11Y-5 + AC #8).

import { useSyncExternalStore } from 'react';

const QUERY = '(prefers-reduced-motion: reduce)';

function getSnapshot(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return window.matchMedia(QUERY).matches;
  } catch {
    return false;
  }
}

function getServerSnapshot(): boolean {
  return false;
}

function subscribe(listener: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => undefined;
  }
  let mql: MediaQueryList;
  try {
    mql = window.matchMedia(QUERY);
  } catch {
    return () => undefined;
  }
  // Modern browsers expose `addEventListener('change', ...)`; older Safari
  // only has `addListener`. We probe for the modern API first.
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', listener);
    return () => {
      mql.removeEventListener('change', listener);
    };
  }
  // Fallback for legacy MediaQueryList.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- legacy API shim
  const legacy = mql as any;
  if (typeof legacy.addListener === 'function') {
    legacy.addListener(listener);
    return () => {
      legacy.removeListener(listener);
    };
  }
  return () => undefined;
}

/**
 * Returns `true` when the user prefers reduced motion. Re-renders consumers
 * when the OS-level preference flips at runtime.
 */
export function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
